
import React, { useState } from 'react';
import { AlertTriangle, Copy, Download, CheckCircle, X, Check } from 'lucide-react';
import { RegisterClientResponseDto } from '../../../../services/api';

interface CredentialsModalProps {
  data: RegisterClientResponseDto;
  onClose: () => void;
}

export const CredentialsModal: React.FC<CredentialsModalProps> = ({ data, onClose }) => {
  const [hasSaved, setHasSaved] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDownload = () => {
    const content = `
CREDENCIALES OAUTH - EL MOZUELO ERP
-----------------------------------
Generado: ${new Date().toLocaleString()}
Cliente: ${data.name}

IMPORTANTE: Estas credenciales son secretas. No las compartas.

Client ID:
${data.clientId}

Client Secret:
${data.clientSecret}

Scopes:
${data.scopes.join(', ')}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oauth-credentials-${data.name.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    // Auto-check the saved checkbox as a convenience or just let them check it
    // Often downloading is considered saving. But let's keep it manual for safety.
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-amber-200">
        <div className="bg-amber-50 p-6 border-b border-amber-100">
          <div className="flex items-center gap-3 text-amber-800 mb-2">
            <AlertTriangle className="h-6 w-6" />
            <h3 className="text-lg font-bold">Cliente OAuth Creado Exitosamente</h3>
          </div>
          <p className="text-amber-700 text-sm">
            IMPORTANTE: Guarda estas credenciales ahora. El <strong>Client Secret</strong> solo se mostrará una vez y no podrá ser recuperado posteriormente.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Client ID */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client ID</label>
            <div className="flex bg-slate-100 rounded-lg border border-slate-200 overflow-hidden">
              <code className="flex-1 p-3 font-mono text-sm text-slate-800 break-all">
                {data.clientId}
              </code>
              <button
                onClick={() => handleCopy(data.clientId, 'id')}
                className="px-3 border-l border-slate-200 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                title="Copiar ID"
              >
                {copiedField === 'id' ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          {/* Client Secret */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client Secret</label>
            <div className="flex bg-amber-50 rounded-lg border border-amber-200 overflow-hidden">
              <code className="flex-1 p-3 font-mono text-sm text-amber-900 break-all font-bold">
                {data.clientSecret}
              </code>
              <button
                onClick={() => handleCopy(data.clientSecret, 'secret')}
                className="px-3 border-l border-amber-200 hover:bg-amber-100 text-amber-700 hover:text-amber-900 transition-colors"
                title="Copiar Secret"
              >
                {copiedField === 'secret' ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
              </button>
            </div>
            <p className="text-xs text-slate-400 text-right">Visible solo esta vez</p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors font-medium border border-slate-200"
            >
              <Download size={18} />
              Descargar .txt
            </button>
          </div>
        </div>

        <div className="p-6 bg-slate-50 border-t border-slate-100">
          <label className="flex items-center gap-3 mb-4 cursor-pointer p-2 rounded hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={hasSaved}
              onChange={(e) => setHasSaved(e.target.checked)}
              className="w-5 h-5 text-green-600 rounded border-slate-300 focus:ring-green-500"
            />
            <span className="text-sm font-medium text-slate-700">
              He guardado las credenciales en un lugar seguro
            </span>
          </label>

          <button
            onClick={onClose}
            disabled={!hasSaved}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:text-slate-500 text-white rounded-lg transition-colors font-bold shadow-sm"
          >
            {hasSaved ? 'Continuar' : 'Confirma que has guardado para continuar'}
          </button>
        </div>
      </div>
    </div>
  );
};
